import {getTestProps} from '@benchling/js-test-utils';
import {Stack, Text} from '@mantine/core';
import React, {useEffect, useState} from 'react';

import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import PersistedDataFrameTableWrapper from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import type {
  PredictionJobOutputDatasetsQuery,
  PredictionJobOutputDatasetsQueryVariables,
} from 'model-hub/__generated/PredictionJobOutputDatasetsQuery';
import {PREDICTION_JOB_OUTPUT_DATASETS_QUERY} from 'model-hub/ModelHubQueries';
import {CenteredSpinner} from 'ui-library/Spinner';

interface OutputDatasetsPreviewProps {
  jobId: string;
  opened: boolean;
  labelClassName?: string;
}

interface OutputDataset {
  id: string;
  name: string;
}

type DatasetsState =
  | {status: 'loading'}
  | {status: 'loaded'; datasets: ReadonlyArray<OutputDataset>}
  | {status: 'failed'};

/** Shows each dataset a completed prediction produced as a paginated data frame table. */
export default function OutputDatasetsPreview({
  jobId,
  opened,
  labelClassName,
}: OutputDatasetsPreviewProps): JSX.Element | null {
  const [state, setState] = useState<DatasetsState>({status: 'loading'});

  useEffect(() => {
    if (!opened || state.status !== 'loading') {
      return;
    }

    let active = true;
    doQuerySafe<PredictionJobOutputDatasetsQuery, PredictionJobOutputDatasetsQueryVariables>(
      {query: PREDICTION_JOB_OUTPUT_DATASETS_QUERY},
      {variables: {id: jobId}, fetchPolicy: 'no-cache'}
    )
      .then((result) => {
        if (active) {
          setState({status: 'loaded', datasets: result.predictionJob?.outputDatasets ?? []});
        }
      })
      .catch(() => {
        if (active) {
          setState({status: 'failed'});
        }
      });

    return () => {
      active = false;
    };
  }, [jobId, opened, state.status]);

  if (!opened) {
    return null;
  }

  return (
    <Stack spacing="xs" {...getTestProps({component: 'OutputDatasetsPreview', element: 'root'})}>
      <Text className={labelClassName}>Output datasets</Text>
      {state.status === 'loading' ? (
        <CenteredSpinner />
      ) : state.status === 'failed' ? (
        <Text color="dimmed" size="sm">
          Output datasets couldn&apos;t be loaded.
        </Text>
      ) : state.datasets.length === 0 ? (
        <Text color="dimmed" size="sm">
          No output datasets for this prediction.
        </Text>
      ) : (
        <Stack spacing="md">
          {state.datasets.map((dataset) => (
            <div key={dataset.id} {...getTestProps({component: 'OutputDatasetsPreview', element: 'dataset'})}>
              <PersistedDataFrameTableWrapper
                actionOrigin={ActionOrigin.MODEL_HUB_PREDICTION_DETAILS_PAGE}
                clientStateId={`model-hub-output-datasets-${jobId}`}
                dataFrameId={dataset.id}
                defaultTableMode={DataFrameTableMode.RICH}
                isCollapsible={true}
              />
            </div>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
